<template>
  <div class="inbox">
    <header class="inbox__head">
      <div class="inbox__title-row">
        <h1 class="inbox__title">Уведомления</h1>
        <span v-if="badgeText" class="inbox__count">{{ badgeText }} непрочитанных</span>
      </div>
      <p class="inbox__lead">Полный список сообщений по разделам. Быстрый просмотр остаётся в колокольчике в шапке.</p>
    </header>

    <div class="inbox__tabs" role="tablist" aria-label="Разделы уведомлений">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        role="tab"
        class="inbox__tab"
        :class="{ 'inbox__tab--active': section === tab.id }"
        :aria-selected="section === tab.id ? 'true' : 'false'"
        @click="setSection(tab.id)"
      >
        {{ tab.label }}
      </button>
    </div>

    <div class="inbox__card">
      <div v-if="view === VIEWS.CHANNEL" class="inbox__toolbar">
        <button type="button" class="inbox__back" @click="backToChannels">
          <font-awesome-icon icon="arrow-left" />
          К каналам
        </button>
        <h2 class="inbox__card-title">{{ panelTitle }}</h2>
        <label class="inbox__toggle" title="Push-уведомления канала">
          <input
            type="checkbox"
            :checked="channelPushEnabled"
            :disabled="togglePending"
            @change="onToggleChannelPush($event.target.checked)"
          />
          <span>уведомления</span>
        </label>
      </div>
      <div v-else class="inbox__toolbar inbox__toolbar--simple">
        <h2 class="inbox__card-title">{{ panelTitle }}</h2>
      </div>

      <div v-if="view === VIEWS.CHANNELS" class="inbox__body">
        <p v-if="channelsPending" class="inbox__hint">Загрузка каналов…</p>
        <p v-else-if="channelsError" class="inbox__error">Не удалось загрузить каналы</p>
        <p v-else-if="!channels.length" class="inbox__hint">Нет каналов</p>
        <ul v-else class="inbox__list">
          <li v-for="ch in channels" :key="ch.id">
            <button type="button" class="inbox__channel" @click="openChannel(ch)">
              <span class="inbox__channel-name">{{ ch.name || `Канал #${ch.id}` }}</span>
              <span v-if="channelUnreadBadge(ch)" class="inbox__channel-unread">{{ channelUnreadBadge(ch) }}</span>
              <font-awesome-icon icon="angles-right" class="inbox__chevron" />
            </button>
          </li>
        </ul>
      </div>

      <NotificationMessageList
        v-else
        :messages="messages"
        :pending="messagesPending"
        :error="messagesError"
        :has-params="hasMessageFilterParams(messagesParams)"
        @select="onMessageClick"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  NOTIFICATION_INBOX_SECTIONS,
  NOTIFICATION_VIEWS as VIEWS,
  hasMessageFilterParams,
  parseInboxChannelId,
  parseInboxSection,
  channelUnreadBadge,
} from '@/components/notifications/notificationLogic.mjs'
import { notificationRoute } from '@/components/notifications/notificationWsLogic.mjs'
import NotificationMessageList from '@/components/notifications/NotificationMessageList.vue'
import { useNotificationInbox } from '@/composables/useNotificationInbox'
import { useUnreadNotifications } from '@/composables/useUnreadNotifications'

const route = useRoute()
const router = useRouter()
const { badgeText } = useUnreadNotifications()

const section = computed(() => parseInboxSection(route.query.section))
const channelId = computed(() => parseInboxChannelId(route.query.channelId))

const view = computed(() => {
  if (section.value === NOTIFICATION_INBOX_SECTIONS.CHANNELS && channelId.value) {
    return VIEWS.CHANNEL
  }
  if (section.value === NOTIFICATION_INBOX_SECTIONS.CHANNELS) return VIEWS.CHANNELS
  if (section.value === NOTIFICATION_INBOX_SECTIONS.ROLE) return VIEWS.ROLE
  return VIEWS.PERSONAL
})

const selectedChannel = computed(() => {
  if (!channelId.value) return null
  return { id: channelId.value }
})

const {
  roleTitle,
  messagesParams,
  messages,
  messagesPending,
  messagesError,
  channels,
  channelsPending,
  channelsError,
  channelPushEnabled,
  togglePending,
  panelTitle,
  onToggleChannelPush,
  markIfUnread,
} = useNotificationInbox({
  active: true,
  view,
  selectedChannel,
})

const tabs = computed(() => [
  { id: NOTIFICATION_INBOX_SECTIONS.PERSONAL, label: 'Личные' },
  { id: NOTIFICATION_INBOX_SECTIONS.ROLE, label: roleTitle.value },
  { id: NOTIFICATION_INBOX_SECTIONS.CHANNELS, label: 'Каналы' },
])

function setSection(next) {
  router.replace({ name: 'Notifications', query: { section: next } })
}

function openChannel(ch) {
  router.push({
    name: 'Notifications',
    query: { section: NOTIFICATION_INBOX_SECTIONS.CHANNELS, channelId: String(ch.id) },
  })
}

function backToChannels() {
  router.replace({
    name: 'Notifications',
    query: { section: NOTIFICATION_INBOX_SECTIONS.CHANNELS },
  })
}

function onMessageClick(msg) {
  markIfUnread(msg)
  const loc = notificationRoute(msg)
  if (!loc) return
  router.push(loc).catch(() => {})
}
</script>

<style scoped>
.inbox {
  max-width: 880px;
  margin: 0 auto;
  padding: 24px 20px 48px;
}

.inbox__head {
  margin-bottom: 20px;
}

.inbox__title-row {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 12px;
}

.inbox__title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
}

.inbox__count {
  font-size: 0.85rem;
  font-weight: 600;
  color: #dc2626;
}

.inbox__lead {
  margin: 8px 0 0;
  font-size: 0.9rem;
  color: #6b7280;
  line-height: 1.45;
}

.inbox__tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.inbox__tab {
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #374151;
  border-radius: 9999px;
  padding: 8px 14px;
  font-size: 0.875rem;
  cursor: pointer;
}

.inbox__tab:hover {
  background: #f9fafb;
}

.inbox__tab--active {
  background: #4f46e5;
  border-color: #4f46e5;
  color: #fff;
}

.inbox__card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
}

.inbox__toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;
  background: #fafafa;
}

.inbox__toolbar--simple {
  justify-content: space-between;
}

.inbox__card-title {
  flex: 1;
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #111827;
}

.inbox__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  color: #4f46e5;
  font-size: 0.85rem;
  cursor: pointer;
  padding: 4px 0;
}

.inbox__back:hover {
  color: #4338ca;
}

.inbox__toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #6b7280;
  cursor: pointer;
  user-select: none;
}

.inbox__body {
  min-height: 200px;
}

.inbox__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.inbox__channel {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 14px 16px;
  border: none;
  border-bottom: 1px solid #f3f4f6;
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: #1f2937;
}

.inbox__channel:hover {
  background: #f9fafb;
}

.inbox__channel-name {
  flex: 1;
  min-width: 0;
  font-size: 0.9rem;
}

.inbox__channel-unread {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 9999px;
  background: #dc2626;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  flex-shrink: 0;
}

.inbox__hint,
.inbox__error {
  margin: 0;
  padding: 36px 16px;
  text-align: center;
  font-size: 0.9rem;
  color: #9ca3af;
}

.inbox__error {
  color: #dc2626;
}

.inbox__chevron {
  color: #9ca3af;
  font-size: 0.75rem;
}
</style>
